import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  createAuthQueryScope,
  queryKeys,
  withAuthQueryScope,
} from "~/lib/query-keys";
import { createBrowserPlatformApiTrpcAdapter } from "~/lib/trpc/platform-api-adapter";
import {
  normalizePortfolioAssetRows,
  toRecord,
} from "~/features/portfolio-setup";

export function useStartFundingAssets(input: {
  authenticated: boolean;
  authQueryScope: ReturnType<typeof createAuthQueryScope>;
  onboardingApi: ReturnType<typeof createBrowserPlatformApiTrpcAdapter>;
  selectedChainId: number;
  walletAddress: string | null;
}) {
  const {
    authenticated,
    authQueryScope,
    onboardingApi,
    selectedChainId,
    walletAddress,
  } = input;

  const userPortfolioQuery = useQuery({
    enabled: authenticated && Boolean(walletAddress),
    queryKey: withAuthQueryScope(
      queryKeys.start.userPortfolio(walletAddress),
      authQueryScope,
    ),
    queryFn: async () =>
      await onboardingApi.userLegacy.getUserPortfolio.query({
        walletAddress: walletAddress!,
      }),
    staleTime: 30_000,
  });

  const filteredTokenListQuery = useQuery({
    enabled: selectedChainId > 0,
    queryKey: queryKeys.start.filteredTokenList(selectedChainId),
    queryFn: async () =>
      await onboardingApi.assetValidation.getFilteredTokenList.query({
        chainId: String(selectedChainId),
      }),
    staleTime: 5 * 60 * 1_000,
  });

  const fundingAssets = useMemo(
    () =>
      normalizePortfolioAssetRows({
        filteredTokenRows: filteredTokenListQuery.data,
        userPortfolioRows: toRecord(userPortfolioQuery.data)
          ?.tokenBalancesWithUsdPrices,
      }),
    [filteredTokenListQuery.data, userPortfolioQuery.data],
  );

  return {
    fundingAssets,
    isError: userPortfolioQuery.isError || filteredTokenListQuery.isError,
    isLoading: userPortfolioQuery.isPending || filteredTokenListQuery.isPending,
    retry: async () => {
      await Promise.all([
        userPortfolioQuery.refetch(),
        filteredTokenListQuery.refetch(),
      ]);
    },
  };
}
